import {
  INTERSECTION_NUMBERS,
  STEP_STORAGE_KEYS,
  TRAFFIC_VOLUME_KEYS,
} from "../../constants/storageConstants";

const VOLUMES_NOT_FOUND_ERROR = {
  message: "Uploaded traffic volumes were empty",
  severity: "error",
};
const WEEKEND_DATA_MISSING_ERROR = {
  message: "Uploaded file is missing weekend traffic volumes",
  severity: "error",
};
const WEEKEND_DATA_SHOULD_NOT_EXIST_ERROR = {
  message:
    "Uploaded file should not have weekend traffic volumes since the active analysis is for weekdays only",
  severity: "error",
};
const VOLUMES_SHOULD_BE_RAMP_TERMINALS_ERROR = {
  message:
    "Uploaded traffic volumes must be formatted for a ramp terminal intersection",
  severity: "error",
};
const VOLUMES_SHOULD_BE_AT_GRADE_ERROR = {
  message:
    "Uploaded traffic volumes must be formatted for an at-grade intersection",
  severity: "error",
};
export const VOLUMES_UPLOAD_SUCCESS = {
  message: "Traffic volumes uploaded",
  severity: "success",
};

/**
 * @param {boolean} isWeekendAnalysis
 * @param {string} paramString dot separated path to a location in the projectData
 * @param {function} saveFormData
 * @param {object} uploadedVolumes object with traffic volumes for the given intersection. has shape {openingYear, designYear}
 * at grade intx have one object and ramp terminals have two (one each for intersection1 and intersection2)
 */
const saveVolumesForIntersection = ({
  isWeekendAnalysis,
  paramString,
  saveFormData,
  uploadedVolumes: { openingYear, designYear },
}) => {
  saveFormData({
    paramString: `${paramString}.${TRAFFIC_VOLUME_KEYS.OPENING_YEAR}`,
    value: {
      amPeak: openingYear.amPeak,
      pmPeak: openingYear.pmPeak,
      ...(isWeekendAnalysis ? { weekendPeak: openingYear.weekendPeak } : {}),
    },
  });
  saveFormData({
    paramString: `${paramString}.${TRAFFIC_VOLUME_KEYS.DESIGN_YEAR}`,
    value: {
      amPeak: designYear.amPeak,
      pmPeak: designYear.pmPeak,
      ...(isWeekendAnalysis ? { weekendPeak: designYear.weekendPeak } : {}),
    },
  });
};

/**
 * @param {object} volumeScenarioObject
 * @returns true if the volume object is incorrectly formatted, or returns false if it is correctly formatted
 */
const isInvalidTrafficVolumesObject = (volumeScenarioObject = {}) => {
  const volumeKeys = Object.keys(volumeScenarioObject);
  const yearNames = Object.values(TRAFFIC_VOLUME_KEYS);
  return (
    volumeKeys.length === 0 ||
    volumeKeys.some((key) => !yearNames.includes(key))
  );
};

/**
 * @param {object} designYear the trafficVolumes data for the designYear
 * @param {boolean} isRampTerminalIntersection
 * @param {boolean} isWeekendAnalysis
 * @param {object} openingYear the trafficVolumes data for the openingYear
 */
const maybeThrowWeekendVolumesError = ({
  designYear,
  isRampTerminalIntersection,
  isWeekendAnalysis,
  openingYear,
} = {}) => {
  if (
    isWeekendAnalysis &&
    (!openingYear.weekendPeak || !designYear.weekendPeak)
  ) {
    throw WEEKEND_DATA_MISSING_ERROR;
  }
  if (
    isRampTerminalIntersection &&
    !isWeekendAnalysis &&
    (openingYear.weekendPeak || designYear.weekendPeak)
  ) {
    throw WEEKEND_DATA_SHOULD_NOT_EXIST_ERROR;
  }
};

/**
 * @param {boolean} isRampTerminalIntersection
 * @param {boolean} isWeekendAnalysis
 * @param {object} trafficVolumes the trafficVolumes data
 */
const isTrafficVolumeDataValid = ({
  isWeekendAnalysis,
  isRampTerminalIntersection,
  trafficVolumes,
}) => {
  if (!trafficVolumes) {
    throw VOLUMES_NOT_FOUND_ERROR;
  }

  if (isRampTerminalIntersection) {
    if (
      isInvalidTrafficVolumesObject(
        trafficVolumes[INTERSECTION_NUMBERS.INTERSECTION_1]
      ) ||
      isInvalidTrafficVolumesObject(
        trafficVolumes[INTERSECTION_NUMBERS.INTERSECTION_2]
      )
    ) {
      throw VOLUMES_SHOULD_BE_RAMP_TERMINALS_ERROR;
    }

    const { intersection1, intersection2 } = trafficVolumes;
    maybeThrowWeekendVolumesError({
      designYear: intersection1.designYear,
      isRampTerminalIntersection,
      isWeekendAnalysis,
      openingYear: intersection1.openingYear,
    });
    maybeThrowWeekendVolumesError({
      designYear: intersection2.designYear,
      isRampTerminalIntersection,
      isWeekendAnalysis,
      openingYear: intersection2.openingYear,
    });
  } else {
    if (isInvalidTrafficVolumesObject(trafficVolumes)) {
      throw VOLUMES_SHOULD_BE_AT_GRADE_ERROR;
    }
    maybeThrowWeekendVolumesError({
      designYear: trafficVolumes.designYear,
      isRampTerminalIntersection,
      isWeekendAnalysis,
      openingYear: trafficVolumes.openingYear,
    });
  }

  return true;
};

/**
 * @param {object} trafficVolumes the trafficVolumes section of projectData
 * @param {boolean} isRampTerminalIntersection
 * @param {boolean} isWeekendAnalysis
 * @param {function} onUpload
 * @param {function} setUploadResult
 * @param {function} saveFormData
 */
export const handleTrafficVolumesUpload = ({
  trafficVolumes,
  isRampTerminalIntersection,
  isWeekendAnalysis,
  onUpload,
  setUploadResult,
  saveFormData,
}) => {
  try {
    // check if uploaded volumes are valid. if they are not, we will throw an error and stop the upload process
    if (
      isTrafficVolumeDataValid({
        isWeekendAnalysis,
        isRampTerminalIntersection,
        trafficVolumes,
      })
    ) {
      if (isRampTerminalIntersection) {
        Object.values(INTERSECTION_NUMBERS).forEach((intersectionNumber) => {
          saveVolumesForIntersection({
            isWeekendAnalysis,
            paramString: `${STEP_STORAGE_KEYS.TRAFFIC_VOLUMES}.${intersectionNumber}`,
            saveFormData,
            uploadedVolumes: trafficVolumes[intersectionNumber],
          });
        });
      } else {
        saveVolumesForIntersection({
          isWeekendAnalysis,
          paramString: STEP_STORAGE_KEYS.TRAFFIC_VOLUMES,
          saveFormData,
          uploadedVolumes: trafficVolumes,
        });
      }

      onUpload(trafficVolumes);
      setUploadResult(VOLUMES_UPLOAD_SUCCESS);
    }
  } catch (error) {
    if (error.severity) {
      setUploadResult(error);
    } else {
      throw error;
    }
  }
};
